import React from "react";
import axios from "axios";
import { Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { STATUS_COLORS, fmtDate } from "./adminShared";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function BookingsTab({ bookings, setBookings, auth }) {
  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, auth);
      setBookings((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, auth);
      setBookings((l) => l.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-bookings-list">
      {bookings.length === 0 && <p className="text-[#8f8571] text-sm py-10 text-center">No bookings yet.</p>}
      {bookings.map((b) => (
        <div key={b.id} className="bg-white border border-[#eee6d6] p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-heading text-lg text-[#171310]">{b.name}</p>
                <Badge className={`rounded-none ${STATUS_COLORS[b.status]} hover:${STATUS_COLORS[b.status]}`}>{b.status}</Badge>
                <span className="text-xs text-[#9c9482]">requested {fmtDate(b.createdAt)}</span>
              </div>
              <p className="text-sm text-[#171310] font-medium mt-1">{b.date} at {b.time}</p>
              <p className="text-sm text-[#5c5240] mt-1">{b.phone}{b.email ? ` · ${b.email}` : ""}</p>
              {b.interest && <p className="text-sm text-[#a8842c] mt-1">Interest: {b.interest}</p>}
              {b.notes && <p className="text-sm text-[#5c5240] mt-2 border-t border-[#f2ecdd] pt-2">{b.notes}</p>}
              {b.flagged && (
                <div className="mt-2.5 border border-[#8c2f1b] bg-[#8c2f1b]/5 px-3 py-2" data-testid="booking-risk-banner">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-[#8c2f1b] uppercase tracking-wider">
                    <ShieldAlert size={13} /> Risk flags
                  </p>
                  <p className="text-xs text-[#8c2f1b] mt-0.5">{(b.flagReasons || []).join(" · ")}</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                <SelectTrigger className="w-[140px] rounded-none border-[#d8cdb5] h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => remove(b.id)} className="rounded-none border-[#d8cdb5] h-9 w-9 text-[#8c2f1b] hover:bg-[#8c2f1b] hover:text-white" aria-label="Delete">
                <Trash2 size={15} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
